import {
  createApiFactory,
  createComponentExtension,
  createPlugin,
  createRoutableExtension,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import {
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
} from '@backstage/plugin-techdocs-react';
import { DocFeedbackClient, docFeedbackApiRef } from './api';
import {
  GiveFeedbackAddon,
  HelpfulAddon,
} from './components/TechDocsAddons';
import { rootRouteRef } from './routes';

/** @public */
export const docFeedbackPlugin = createPlugin({
  id: 'doc-feedback',
  routes: {
    root: rootRouteRef,
  },
  apis: [
    createApiFactory({
      api: docFeedbackApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new DocFeedbackClient({ discoveryApi, fetchApi }),
    }),
  ],
});

/**
 * Triage page listing feedback for the user's teams, all feedback (Platform
 * Engineering) and the user's own submissions.
 *
 * @public
 */
export const DocFeedbackPage = docFeedbackPlugin.provide(
  createRoutableExtension({
    name: 'DocFeedbackPage',
    component: () =>
      import('./components/DocFeedbackPage').then(m => m.DocFeedbackPage),
    mountPoint: rootRouteRef,
  }),
);

/**
 * Entity card with open feedback and page helpfulness for the entity's docs.
 *
 * @public
 */
export const EntityDocFeedbackCard = docFeedbackPlugin.provide(
  createComponentExtension({
    name: 'EntityDocFeedbackCard',
    component: {
      lazy: () =>
        import('./components/EntityDocFeedbackCard').then(
          m => m.EntityDocFeedbackCard,
        ),
    },
  }),
);

/**
 * TechDocs addon adding a "Give feedback" button to the docs subheader.
 *
 * @public
 */
export const DocFeedbackAddon = docFeedbackPlugin.provide(
  createTechDocsAddonExtension({
    name: 'DocFeedback',
    location: TechDocsAddonLocations.Subheader,
    component: GiveFeedbackAddon,
  }),
);

/**
 * TechDocs addon adding a "Was this page helpful?" bar at the bottom of each page.
 *
 * @public
 */
export const DocHelpfulAddon = docFeedbackPlugin.provide(
  createTechDocsAddonExtension({
    name: 'DocHelpful',
    location: TechDocsAddonLocations.Content,
    component: HelpfulAddon,
  }),
);
